"use client";
import { useAtomValue, useAtom } from "jotai";
import dynamic from "next/dynamic";
import { ReactFlow, SelectionMode, ConnectionMode, Node } from "@xyflow/react";
import { ReactNode, useRef, useMemo, useEffect } from "react";
import {
  cursorAtom,
  edgesAtom,
  isViewOnlyModeAtom,
  nodesAtom,
  selectedNodeTypeAtom,
  horizontalHelperLineAtom,
  verticalHelperLineAtom,
  isViewerAtom,
  isPublicAtom,
  togglesAtomResettable,
  editingNodeAtom,
  previewNodeIdAtom,
  composerServiceAtom,
} from "@zapier/canvas-state";
import classNames from "classnames";

import isConnectingTwoZapSteps from "../../utils/isConnectingTwoZapSteps";
import { ThirdPartyIntegrations } from "../../components/ThirdPartyIntegrations";
import useRemoveTempNodesAndEdges from "../../hooks/useRemoveTempNodesAndEdges";
import { useCanvasEmbedEvents } from "../../hooks/useCanvasEmbedEvents";
import { areElementsSelectable } from "../../utils/elementsSelectable";

import { AssetDetailModal } from "../AssetDetailModal";
import { WhatsNewAlert } from "../WhatsNewAlert";

import { Panel } from "./Panel";
import { nodeTypes } from "./nodeTypes";
import { edgeTypes } from "./edgeTypes";
import { useReactFlowCallbacks } from "./useReactFlowCallbacks";
import { useDeleteCallbacks } from "./useDeleteCallbacks";
import { HelperLines } from "./HelperLines";
import { useOnNodeClick } from "./useOnNodeClick";
import { useOnSelection } from "./useOnSelection";
import { useOnPaneClick } from "./useOnPaneClick";
import ErrorBoundary from "./ErrorBoundary";
import { getCursorType, getGroupNodesFirst } from "./utils";
import { useCopyPaste } from "./useCopyPaste";
import { useExecuteAiPrompt } from "../ExecuteAiPrompt";
import {
  aiPromptAtom,
  fixAndParseJSON,
  zslOutlineToNodesAndEdges,
} from "@zapier/streaming";
import {
  partialOutlineAtom,
  outlineAtom,
} from "@zapier/streaming/src/atoms/outlineAtom";
import { KeydownListeners } from "../KeydownListeners";
import { useSavedChatState } from "../../hooks/useSavedChatState";
import { useSearchParams } from "next/navigation";
import { SELECTED_CHAT_ID_QUERY_PARAM } from "@zapier/canvas-constants";

const Sidebar = dynamic(() => import("./Sidebar").then((mod) => mod.Sidebar), {
  ssr: false,
});

type Props = {
  children: ReactNode;
};

export function Canvas({ children }: Props) {
  const [editingNode, setEditingNode] = useAtom(editingNodeAtom);
  const toggles = useAtomValue(togglesAtomResettable);
  const cursorMode = useAtomValue(cursorAtom);
  const prompt = useAtomValue(aiPromptAtom);
  const [nodes, setNodes] = useAtom(nodesAtom);
  const [edges, setEdges] = useAtom(edgesAtom);
  const searchParams = useSearchParams();
  const chatId = searchParams?.get(SELECTED_CHAT_ID_QUERY_PARAM);
  useExecuteAiPrompt(prompt);
  useSavedChatState(chatId ?? undefined);

  const ref = useRef<HTMLDivElement | null>(null);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const isViewer = useAtomValue(isViewerAtom);
  const isPublic = useAtomValue(isPublicAtom);
  const selectedNodeType = useAtomValue(selectedNodeTypeAtom);
  const hasSelectedNodeType = Boolean(selectedNodeType);
  const helperLineHorizontal = useAtomValue(horizontalHelperLineAtom);
  const helperLineVertical = useAtomValue(verticalHelperLineAtom);
  const previewNodeId = useAtomValue(previewNodeIdAtom);

  useCopyPaste();

  const {
    onNodesChange,
    onEdgesChange,
    onNodeContextMenu,
    onPaneMouseMove,
    onInitSetViewport,
    onConnect,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    onConnectStart,
    onConnectEnd,
    onReconnectStart,
    onReconnectEnd,
    onReconnect,
  } = useReactFlowCallbacks(ref);
  const { onNodeClick } = useOnNodeClick(ref);
  const { onSelectionStart, onSelectionEnd } = useOnSelection();
  const { onPaneClick } = useOnPaneClick(ref);
  const outlineData = useAtomValue(outlineAtom);
  const partialOutlineData = useAtomValue(partialOutlineAtom);
  const composerService = useAtomValue(composerServiceAtom);
  useRemoveTempNodesAndEdges();

  const { onNodeDeleteFromKeyboard, onEdgesDeleteFromKeyboard } =
    useDeleteCallbacks();

  useCanvasEmbedEvents();
  const handlePanelClose = () => {
    setEditingNode(null);
    if (toggles.aiComposerDataPills) {
      // Reset the placeholder text to default
      composerService?.setPlaceholderText(null);

      //clean any "viewing: ${assetType}" keys
      composerService?.setAdditionalData(
        (existingData: Record<string, any> | undefined) => {
          const newData = { ...existingData };
          Object.keys(newData).forEach((key) => {
            if (key.startsWith("viewing: ")) {
              delete newData[key];
            }
          });
          return newData;
        },
      );
    }
  };
  // In xyflow, your group nodes have to exist in the array before their children.
  // Otherwise, you get this error:
  // Parent node `id` not found. Please make sure that parent nodes are in front of their child nodes in the nodes array.
  const nodesWithParentsFirst = useMemo(() => {
    return getGroupNodesFirst(nodes);
  }, [nodes]);

  /* This effect is used to update the canvas when the outline data is set.
   * Taken from this discussion https://github.com/pmndrs/jotai/discussions/271 as a valid solution to keep the react tree in sync with a jotai atom updated from a stream.
   */
  useEffect(() => {
    const parsedOutlineData = fixAndParseJSON(outlineData);
    const parsedPartialOutlineData = fixAndParseJSON(partialOutlineData);
    const isOutlineDataSet = Object.keys(parsedOutlineData).length > 0;
    const parsedData = isOutlineDataSet
      ? parsedOutlineData
      : parsedPartialOutlineData;
    const { nodes, edges } = zslOutlineToNodesAndEdges(parsedData);
    if (nodes.length > 0) {
      setNodes(nodes);
      setEdges(edges);
    }
  }, [partialOutlineData, outlineData, setNodes, setEdges]);
  return (
    <>
      <ErrorBoundary>
        {toggles.showWhatsNewAlert.isOn && <WhatsNewAlert />}
        {previewNodeId && <AssetDetailModal previewNodeId={previewNodeId} />}
        <ThirdPartyIntegrations />
        <KeydownListeners />
        <ReactFlow
          ref={ref}
          connectionMode={ConnectionMode.Loose}
          nodes={nodesWithParentsFirst as Node[]}
          edges={edges}
          elementsSelectable={areElementsSelectable({
            isViewOnlyMode,
            isViewer,
            isPublic,
          })}
          proOptions={{ hideAttribution: true }}
          selectionOnDrag={true}
          onNodesDelete={onNodeDeleteFromKeyboard}
          onEdgesDelete={onEdgesDeleteFromKeyboard}
          minZoom={0.25}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          panOnScroll={cursorMode !== "pan"}
          onNodeContextMenu={onNodeContextMenu}
          onPaneClick={onPaneClick}
          onPaneMouseMove={onPaneMouseMove}
          onMoveStart={() => {
            /* To prevent this bug in floating toolbar where popover menu is disconnected from the input
            when you pan: https://cdn.zappy.app/f78f666d913ec41747b8c4867e3ec1d9.gif */
            window.dispatchEvent(new Event("scroll", { bubbles: true }));
          }}
          onSelectionStart={onSelectionStart}
          onSelectionEnd={onSelectionEnd}
          onInit={onInitSetViewport}
          onNodeClick={onNodeClick}
          className={classNames(
            { "view-only-mode": isViewOnlyMode },
            {
              [`${getCursorType(cursorMode, hasSelectedNodeType)}-cursor`]:
                getCursorType(cursorMode, hasSelectedNodeType),
            },
          )}
          panOnDrag={
            cursorMode === "pan" ||
            (selectedNodeType !== null && selectedNodeType !== "group")
          }
          selectionMode={SelectionMode.Partial}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          onNodeDragStart={onNodeDragStart}
          onNodeDrag={onNodeDrag}
          onNodeDragStop={onNodeDragStop}
          onReconnectStart={onReconnectStart}
          onReconnectEnd={onReconnectEnd}
          onReconnect={onReconnect}
          nodeDragThreshold={10}
          connectionRadius={100}
          isValidConnection={(connection) =>
            !isConnectingTwoZapSteps(connection)
          }
          deleteKeyCode={["Backspace", "Delete"]}
          snapToGrid={true}
          snapGrid={[10, 10]}
          nodesDraggable={!isViewOnlyMode}
          edgesReconnectable={!isViewOnlyMode}
        >
          {children}
          <HelperLines
            horizontal={helperLineHorizontal}
            vertical={helperLineVertical}
          />
        </ReactFlow>
        {editingNode && (
          <Panel onClose={handlePanelClose} nodeId={editingNode} />
        )}
        {/* This for the React portal to attach to.
        This is so events are no longer trapped by ReactFlow */}
        <div id="toolbar-content" />
        <Sidebar />
      </ErrorBoundary>
    </>
  );
}
